import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import {
  Gauge, CalendarCheck, Ticket, Receipt, Exam, Trophy, Certificate, BookOpen, UserCircle, Bell, Lifebuoy,
  MapPin, Buildings, CalendarPlus, Users, QrCode, IdentificationBadge, Tag, Article, Question, ShoppingCart,
  Funnel, Headset, UserGear, ClockCounterClockwise, Books, Percent, Medal, GraduationCap, ChatCircleText, Gift, ShareNetwork, NotePencil, ShieldWarning, UserMinus, Gear, ChartLine, CalendarBlank,
} from "@phosphor-icons/react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, isStaff, useAuth } from "@/context/AuthContext";
import PublicLayout from "@/components/layout/PublicLayout";
import DashShell from "@/components/layout/DashShell";
import Home from "@/pages/public/Home";
import { Courses, CourseDetail } from "@/pages/public/Courses";
import { Cities, CityPage } from "@/pages/public/Cities";
import EventDetail from "@/pages/public/EventDetail";
import Checkout, { PaymentSuccess, PaymentCancel } from "@/pages/public/Checkout";
import { Pricing, MissionControl, About, FreeSeminar, TraderQuiz, Faq, Contact, LegalPage, Verify, NotFound } from "@/pages/public/Pages";
import { Login, Register, ForgotPassword, ResetPassword } from "@/pages/public/Auth";
import { StudentDashboard, Registrations, Tickets, Receipts, ReceiptDetail } from "@/pages/app/StudentA";
import { Quizzes, QuizTake, Badges, Certificates, Materials, Profile, Notifications, Support } from "@/pages/app/StudentB";
import { AdminDashboard, Checkin, AdminRegistrations, BadgePrint } from "@/pages/admin/AdminA";
import { Students, StudentDetail, Orders, Leads, SupportDesk, Users as AdminUsers, Audit } from "@/pages/admin/AdminB";
import AdminResource from "@/pages/admin/AdminResource";
import AdminReferrals from "@/pages/admin/Referrals";
import StudentReferrals from "@/pages/app/Referrals";
import ReferralLanding from "@/pages/public/Referral";
import Leaderboard, { ContestRules } from "@/pages/public/Leaderboard";
import { VerifyEmail, HowWeMeasure, LaunchSettings } from "@/pages/LaunchPages";
import { CookieBanner } from "@/components/CookieBanner";
import LunchSheet from "@/pages/admin/LunchSheet";
import FlightLog from "@/pages/app/FlightLog";
import GiftCards, { GiftCardSuccess } from "@/pages/public/GiftCards";
import { Disputes, GiftCardsAdmin, NoShows, FlightLogsAdmin, FlightLogStudent } from "@/pages/admin/Protect";
import IndicatorAdmin from "@/pages/admin/IndicatorAdmin";
import SeasonsAdmin from "@/pages/admin/SeasonsAdmin";

const STUDENT_NAV = [
  ["/app", "Dashboard", Gauge, true], ["/app/registrations", "Registrations", CalendarCheck], ["/app/tickets", "Tickets", Ticket],
  ["/app/receipts", "Receipts", Receipt], ["/app/quizzes", "Quizzes", Exam], ["/app/badges", "Badges", Trophy],
  ["/app/certificates", "Certificates", Certificate], ["/app/materials", "Materials", BookOpen], ["/app/flight-log", "Flight log", NotePencil], ["/app/referrals", "Bring a friend", Gift], ["/app/notifications", "Messages", Bell],
  ["/app/support", "Support", Lifebuoy], ["/app/profile", "Profile", UserCircle],
];

const PERMS = {
  admin: ["dashboard", "events", "catalog", "students", "orders", "refunds", "content", "coupons", "quizzes", "support", "checkin", "badges_print", "audit", "users", "referrals"],
  instructor: ["dashboard", "events", "students", "checkin", "quizzes"],
  checkin: ["dashboard", "checkin", "badges_print"],
  support: ["dashboard", "support", "students", "orders"],
};
const ADMIN_NAV = [
  ["/superadmin", "Dashboard", Gauge, true, "dashboard"], ["/superadmin/checkin", "Check-in", QrCode, false, "checkin"],
  ["/superadmin/badges", "Name badges", IdentificationBadge, false, "badges_print"], ["/superadmin/events", "Events", CalendarPlus, false, "events"],
  ["/superadmin/registrations", "Registrations", CalendarCheck, false, "events"], ["/superadmin/cities", "Cities", MapPin, false, "events"],
  ["/superadmin/venues", "Venues", Buildings, false, "events"], ["/superadmin/students", "Students", Users, false, "students"],
  ["/superadmin/leads", "Leads & waitlists", Funnel, false, "students"], ["/superadmin/referrals", "Referrals", ShareNetwork, false, "referrals"], ["/superadmin/orders", "Orders & refunds", ShoppingCart, false, "orders"],
  ["/superadmin/disputes", "Chargebacks", ShieldWarning, false, "refunds"], ["/superadmin/gift-cards", "Gift cards", Gift, false, "orders"],
  ["/superadmin/flight-logs", "Flight logs", NotePencil, false, "students"], ["/superadmin/indicator", "Indicator access", ChartLine, false, "students"], ["/superadmin/seasons", "Seasons & pricing", CalendarBlank, false, "events"],
  ["/superadmin/no-shows", "No-show report", UserMinus, false, "events"],
  ["/superadmin/launch-settings", "Launch settings", Gear, false, "users"],
  ["/superadmin/support", "Support desk", Headset, false, "support"], ["/superadmin/courses", "Courses", GraduationCap, false, "catalog"],
  ["/superadmin/coupons", "Coupons", Tag, false, "coupons"], ["/superadmin/quizzes", "Quizzes", Exam, false, "quizzes"],
  ["/superadmin/badge-types", "Badge types", Medal, false, "quizzes"], ["/superadmin/materials", "Materials", Books, false, "content"],
  ["/superadmin/faqs", "FAQs", Question, false, "content"], ["/superadmin/testimonials", "Testimonials", ChatCircleText, false, "content"],
  ["/superadmin/pages", "Legal pages", Article, false, "content"], ["/superadmin/tax", "Tax profiles", Percent, false, "owner"],
  ["/superadmin/users", "Users & roles", UserGear, false, "users"], ["/superadmin/audit", "Audit log", ClockCounterClockwise, false, "audit"],
];

const allowed = (user, p) => user?.role === "owner" || (PERMS[user?.role] || []).includes(p);

const G = ({ p, children }) => {
  const { user } = useAuth();
  if (allowed(user, p)) return children;
  return <div className="panel p-8" data-testid="no-access"><h1 className="font-display text-3xl font-black uppercase">No access</h1><p className="mt-2 text-sm text-[#475569]">Your role does not include this area. Ask the owner if you need it.</p></div>;
};

const RoleAwareAdmin = () => {
  const { user } = useAuth();
  const items = !user ? ADMIN_NAV : ADMIN_NAV.filter(([, , , , p]) => allowed(user, p));
  return <DashShell label="Superadmin" allow={isStaff} items={items} />;
};

const ScrollTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const LEGAL = ["terms", "privacy", "refund-policy", "risk-disclosure", "accessibility", "referral-terms"];

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollTop />
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/courses" element={<Courses />} />
            <Route path="/courses/:slug" element={<CourseDetail />} />
            <Route path="/cities" element={<Cities />} />
            <Route path="/cities/:slug" element={<CityPage />} />
            <Route path="/events/:slug" element={<EventDetail />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/mission-control" element={<MissionControl />} />
            <Route path="/about" element={<About />} />
            <Route path="/free-seminar" element={<FreeSeminar />} />
            <Route path="/quiz" element={<TraderQuiz />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/verify" element={<Verify />} />
            <Route path="/verify/:code" element={<Verify />} />
            {LEGAL.map((s) => <Route key={s} path={`/${s}`} element={<LegalPage key={s} slug={s} />} />)}
            <Route path="/r/:code" element={<ReferralLanding />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/leaderboard/contest-rules" element={<ContestRules />} />
            <Route path="/gift-cards" element={<GiftCards />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/how-we-measure" element={<HowWeMeasure />} />
            <Route path="/gift-cards/success" element={<GiftCardSuccess />} />
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/app" element={<DashShell label="Student App" items={STUDENT_NAV} />}>
            <Route index element={<StudentDashboard />} />
            <Route path="registrations" element={<Registrations />} />
            <Route path="tickets" element={<Tickets />} />
            <Route path="receipts" element={<Receipts />} />
            <Route path="receipts/:id" element={<ReceiptDetail />} />
            <Route path="quizzes" element={<Quizzes />} />
            <Route path="quizzes/:slug" element={<QuizTake />} />
            <Route path="badges" element={<Badges />} />
            <Route path="certificates" element={<Certificates />} />
            <Route path="materials" element={<Materials />} />
            <Route path="flight-log" element={<FlightLog />} />
            <Route path="referrals" element={<StudentReferrals />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="support" element={<Support />} />
            <Route path="profile" element={<Profile />} />
          </Route>
          <Route path="/superadmin" element={<RoleAwareAdmin />}>
            <Route index element={<AdminDashboard />} />
            <Route path="checkin" element={<G p="checkin"><Checkin /></G>} />
            <Route path="badges" element={<G p="badges_print"><BadgePrint /></G>} />
            <Route path="registrations" element={<G p="events"><AdminRegistrations /></G>} />
            <Route path="catering/:eventId" element={<G p="events"><LunchSheet /></G>} />
            <Route path="students" element={<G p="students"><Students /></G>} />
            <Route path="students/:id" element={<G p="students"><StudentDetail /></G>} />
            <Route path="leads" element={<G p="students"><Leads /></G>} />
            <Route path="referrals" element={<G p="referrals"><AdminReferrals /></G>} />
            <Route path="orders" element={<G p="orders"><Orders /></G>} />
            <Route path="disputes" element={<G p="refunds"><Disputes /></G>} />
            <Route path="gift-cards" element={<G p="orders"><GiftCardsAdmin /></G>} />
            <Route path="flight-logs" element={<G p="students"><FlightLogsAdmin /></G>} />
            <Route path="flight-logs/:uid" element={<G p="students"><FlightLogStudent /></G>} />
            <Route path="indicator" element={<G p="students"><IndicatorAdmin /></G>} />
            <Route path="seasons" element={<G p="events"><SeasonsAdmin /></G>} />
            <Route path="no-shows" element={<G p="events"><NoShows /></G>} />
            <Route path="launch-settings" element={<G p="users"><LaunchSettings /></G>} />
            <Route path="support" element={<G p="support"><SupportDesk /></G>} />
            <Route path="users" element={<G p="users"><AdminUsers /></G>} />
            <Route path="audit" element={<G p="audit"><Audit /></G>} />
            {[["events", "events", "events"], ["cities", "cities", "events"], ["venues", "venues", "events"], ["courses", "courses", "catalog"], ["coupons", "coupons", "coupons"], ["quizzes", "quizzes", "quizzes"],
              ["badge-types", "badges", "quizzes"], ["materials", "materials", "content"], ["faqs", "faqs", "content"], ["testimonials", "testimonials", "content"], ["pages", "pages", "content"], ["tax", "tax_profiles", "owner"]].map(([p, r, perm]) => (
              <Route key={p} path={p} element={<G p={perm}><AdminResource name={r} /></G>} />
            ))}
          </Route>
        </Routes>
        <Toaster position="bottom-right" richColors />
        <CookieBanner />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
